import { useCallback, useContext, useEffect, useId, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ReactComponent as EditIcon } from "assets/images/common/edit.svg";
import { ReactComponent as TrashIcon } from "assets/images/common/trash.svg";

import { LoaderContext } from "context/GlobalLoader";

import Button from "components/Button";
import Card from "components/Card";
import Tabs from "components/Tabs";

import AssetDevicesTab from "modules/assets/DevicesTab";
import AssetInfoTab from "modules/assets/AssetInfoTab";
import AssetIncidentsTab from "modules/assets/IncidentsTab";
import AssetOverviewTab from "modules/assets/OverviewTab";
import PageTemplate from "modules/common/PageTemplate";

import { AssetFormValues, ModalType } from "entities/common";
import { UpdateAssetRequest } from "entities/assets";
import { DevicesApi } from "services/http";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import {
  getAssetRequest,
  resetSuccessfullyUpdatedAsset,
  updateAssetRequest,
} from "store/slices/assetSlice";
import { showToast } from "store/slices/toastSlice";

import styles from "./style.module.scss";

const TABS = [
  { label: "Overview", value: "overview" },
  { label: "Asset info", value: "asset-info" },
  { label: "Incidents", value: "incidents" },
  { label: "Devices", value: "devices" },
];

const AssetDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { [QUERY_PARAMS.TAB]: tabParam } = QueryHelper.getFromQueryString(location.search);
  const [activeTab, setActiveTab] = useState(tabParam || "overview");
  const [deviceCount, setDeviceCount] = useState<number | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    DevicesApi.getStatistics(`?asset_id=${id}`)
      .then(({ data }) => {
        if (active) setDeviceCount(data.total);
      })
      .catch(() => {
        if (active) setDeviceCount(null);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const tabs = TABS.map((tab) =>
    tab.value === "devices" ? { ...tab, notification: deviceCount || undefined } : tab,
  );
  const callbackKey = useId();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      navigate(
        `${PAGES.ASSET_DETAIL.replace(":id", id?.toString() || "")}${QueryHelper.getQueryString({
          [QUERY_PARAMS.TAB]: tab,
        })}`,
        { state: location.state },
      );
    },
    [navigate, id, location.state],
  );
  const { handleLoad } = useContext(LoaderContext);
  const {
    isCurrentLoading,
    current: asset,
    successfullyUpdatedId,
    successfullyRemovedId,
  } = useAppSelector((state) => state.assets);

  const canManage = asset?.can_manage ?? false;

  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const backLink =
    returnTo ||
    (asset?.portfolio_id ? PAGES.PORTFOLIO_DETAIL.replace(":id", String(asset.portfolio_id)) : PAGES.ASSETS_LIST);

  const address = [
    asset?.company_city?.label,
    asset?.city,
    asset?.area,
    asset?.building_name,
    asset?.unit_number,
    asset?.street,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

  const handleSaveAsset = (values?: unknown) => {
    if (!asset) return;

    const data = values as AssetFormValues;

    const body: UpdateAssetRequest = {
      name: data.name,
      type: data.type || null,
      description: data.description || null,
      portfolio_id: asset.portfolio_id,
      manager_id: Number(data.managerId),
    };

    const remainingExistingIds = new Set(
      data.images
        .filter((img) => !img.file && img.id !== undefined && img.id !== null)
        .map((img) => Number(img.id))
        .filter((n) => !Number.isNaN(n)),
    );
    const removedImageIds = (asset.images || [])
      .map((img) => img.id)
      .filter((existingId) => !remainingExistingIds.has(existingId));
    const newImages = data.images.filter((img) => Boolean(img.file));

    dispatch(updateAssetRequest({ id: asset.id, body, newImages, removedImageIds }));
  };

  const handleEditAsset = () => {
    if (!asset) return;

    setModalCallbacks(callbackKey, { onSave: handleSaveAsset });

    dispatch(
      showModal({
        modalType: ModalType.CREATE_ASSET,
        modalProps: {
          mode: "edit",
          id: asset.id,
          callbackKey,
          companyId: asset.company_id ?? undefined,
          initialData: {
            name: asset.name || "",
            type: asset.type || "",
            description: asset.description || "",
            number: "",
            managerId: asset.manager_id ? String(asset.manager_id) : "",
            images: (asset.images || []).map((img) => ({ id: String(img.id), url: img.url })),
          },
        },
      }),
    );
  };

  const handleDeleteAsset = () => {
    if (!asset) return;

    dispatch(
      showModal({
        modalType: ModalType.REMOVE_ASSET,
        modalProps: { id: asset.id, name: asset.name },
      }),
    );
  };

  useEffect(() => {
    dispatch(getAssetRequest(Number(id)));
  }, [dispatch, id]);

  useEffect(() => {
    handleLoad(isCurrentLoading);

    return () => {
      handleLoad(false);
    };
  }, [isCurrentLoading, handleLoad]);

  useEffect(() => {
    if (successfullyUpdatedId) {
      dispatch(hideModal());
      dispatch(resetSuccessfullyUpdatedAsset());
      dispatch(getAssetRequest(Number(id)));
      dispatch(
        showToast({
          id: "successfully-updated-asset",
          message: "The asset was successfully updated.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyUpdatedId, dispatch, id]);

  useEffect(() => {
    if (successfullyRemovedId) {
      navigate(backLink);
    }
  }, [successfullyRemovedId, dispatch, navigate, backLink]);

  return (
    <PageTemplate
      isBackButton
      backButtonLink={backLink}
      title={asset?.name || ""}
      description={address}
      className={styles.page}
      actions={
        <div className={styles.actions}>
          {canManage && (
            <Button variant="white" form="circle" onClick={handleEditAsset}>
              <EditIcon />
            </Button>
          )}

          {canManage && (
            <Button variant="white" form="circle" onClick={handleDeleteAsset}>
              <TrashIcon />
            </Button>
          )}
        </div>
      }
    >
      <Card className={styles.card}>
        <Tabs tabs={tabs} value={activeTab} onChange={handleTabChange} />

        <div className={styles.content}>
          {activeTab === "overview" && <AssetOverviewTab />}
          {activeTab === "asset-info" && <AssetInfoTab />}
          {activeTab === "incidents" && <AssetIncidentsTab />}
          {activeTab === "devices" && <AssetDevicesTab />}
        </div>
      </Card>
    </PageTemplate>
  );
};

export default AssetDetailPage;
